import {
	IRequestWithPageSortFilterSearch,
	TFiltersQ4Us,
	TOrderByQ4Us,
	TPageResponse,
} from 'redux/types';

// ==========================================
// Store type
// ==========================================

export type TInvoiceManagementStore = {
	invoiceList: TInvoiceListResponsePayload;
	invoiceListLoading: boolean;
	downloadInvoicePdfLoader: boolean;
	downloadedInvoicePdf: TDownloadInvoicePdfResponsePayload | null;
	downloadInvoiceUdrCsvLoader: boolean;
	downloadedInvoiceUdrCsv: TDownloadInvoiceUdrCsvResponsePayload | null;
	updateInvoicePaymentLoader: boolean;
	updateInvoiceCommentLoader: boolean;
	sendEmailForInvoiceLoader: boolean;
	updateInvoiceGpsrLoader: boolean;
	encryptInvoiceNumberLoader: boolean;
	encryptInvoiceNumber: TEncryptInvoiceNumberResponsePayload;
	nullifyInvoiceLoader: boolean;
	removeInvoiceLpfLoader: boolean;
	invoiceListExcelExportLoader: boolean;
	invoiceListExcelExport: TInvoiceListExcelExportResponsePayload;
	downloadInvoiceArchiveLoader: boolean;
	creditNotes: TCreditNoteListResponse | null;
	creditNotesLoading: boolean;
	creditNoteDetails: TCreditNote | null;
	creditNoteDetailsLoading: boolean;
};

// ==========================================
// Invoice List
// ==========================================

export enum EInvoiceServiceType {
	ADDONS = 'addons',
	GPSR = 'gpsr',
	EAS = 'eas',
	SWISS = 'swiss',
	EUDR = 'eudr',
	EU_DDP = 'eu_ddp',
}

export type TInvoiceListRequest = {
	tableName: string;
	orderBy?: TOrderByQ4Us;
	filter?: TFiltersQ4Us;
	page: { size: number; from: number };
};

export type TInvoiceListItem = {
	prepaid_amount: number;
	invoice_id: number;
	em_id: number;
	invoice_duration: string;
	communication_status: string;
	invoice_number: number;
	timestamp: string;
	invoice_carry_over_amount: number;
	reference_number: number;
	client: string;
	vat_number: string;
	client_number: string;
	country: string;
	legal_status_with_reg_country: string;
	due_date: string;
	total_invoice_amount_in_eur: number;
	total_invoice_amount_in_client_currency: number | null;
	client_currency: string | null;
	due_interest_amount: number;
	invoiced_amount_in_eur: number;
	invoiced_amount_in_client_currency: number | null;
	paid_amount: number | null;
	date_of_payment: string | null;
	eas_fee_with_invoice_fee: string | null;
	vat_rate: number | null;
	vat_on_eas_fee_and_invoicing_fee: string;
	eas_fee_with_vat_on_eas_fee: string | null;
	vat_due_to_eas: number;
	shipment_fee_due: number;
	customs_duty_due: number;
	payment_status: string;
	comment: string | null;
	year: number;
	month: number;
	invoice_date: string;
	scheme_list: (string | null)[];
	carry_over_amount: number;
	zero_invoice: boolean;
	has_reimbursments: boolean;
	is_gpsr: boolean;
	invoicing_fee_name: string;
	service_type: EInvoiceServiceType;
	non_union_plan_amount: number | null;
	non_union_plan_name: string | null;
	revolut_invoiced_amount: string;
	revolut_received_amount: string;
	revolut_fee: string;
	reimbursed_amount: number | null;
	vat_reimbursement_list_import: {
		[key: string]: number;
	}[];
	reimbursed_countries: string;
	delayed_invoice_number: number | null;
	invoicing_fee: number | null;
	monthly_fee: number | null;
	monthly_fee_class_name: string | null;
	non_eu_import_vat_eur: number | null;
	vat_fiscal_import: number | null;
	vat_fiscal_union: number | null;
	vat_fiscal_non_union: number | null;
	fc_rep_vat_eur: number | null;
	paid_thruogh: number | null;
	email: string | null;
};

export type TInvoiceListPage = {
	current: number;
	size: number;
	total: number;
	total_amount_in_eur_invoices: number;
	total_invoiced_amount_in_eur_invoices: number;
	total_paid_amount_in_eur: number;
	balance_in_eur: string;
};

export type TInvoiceListResponse = {
	statusCode: number;
	rows: TInvoiceListItem[];
	rowCount: number;
	page: TInvoiceListPage;
};

export type TInvoiceListResponsePayload = TInvoiceListResponse | null;

// ==========================================
// Download Invoice PDF
// ==========================================

export type TDownloadInvoicePdfRequest = {
	emId: number;
	invoiceId: number;
};

export type TDownloadInvoicePdfResponse = Blob;

export type TDownloadInvoicePdfResponsePayload = TDownloadInvoicePdfResponse;

// ==========================================
// Download Invoice UDR CSV
// ==========================================

export type TDownloadInvoiceUdrCsvRequest = {
	emId: number;
	invoiceId: number;
};

export type TDownloadInvoiceUdrCsvResponse = Blob;

export type TDownloadInvoiceUdrCsvResponsePayload = TDownloadInvoiceUdrCsvResponse;

// ==========================================
// Update Invoice Payment
// ==========================================

export type TUpdateInvoicePaymentBody = {
	invoice_id: number;
	payment_date: string;
	amount: number;
	paid_amount: number;
	paid_thruogh: string;
};

export type TUpdateInvoicePaymentRequest = {
	body: TUpdateInvoicePaymentBody;
	invoiceListRequest: TInvoiceListRequest;
};

export type TUpdateInvoicePaymentResponse = {
	data: {
		get_invoice_status: {
			rows: {
				get_invoice_payment_status: string;
			}[];
			rowCount: number;
		};
		update_invoice_payment?: {
			rows: unknown[];
			rowCount: number;
		};
		update_payment_status_of_invoice?: {
			rows: unknown[];
			rowCount: number;
		};
	};
	status: number;
	execTime: number;
};

export type TUpdateInvoicePaymentResponsePayload = TUpdateInvoicePaymentResponse;

// ==========================================
// Update Invoice Comment
// ==========================================

export type TUpdateInvoiceCommentBody = {
	invoice_id: number;
	comment_content: string;
};

export type TUpdateInvoiceCommentRequest = {
	body: TUpdateInvoiceCommentBody;
	invoiceListRequest: TInvoiceListRequest;
};

export type TUpdateInvoiceCommentResponse = {
	[key: string]: unknown;
};

export type TUpdateInvoiceCommentResponsePayload = TUpdateInvoiceCommentResponse;

// ==========================================
// Send Email For Invoice
// ==========================================

export type TSendEmailForInvoiceBody = {
	invoice_id: number;
	invoice_number: number;
	timestamp: string;
	client_number: string;
	due_date: string;
	reference_number: number;
	payment_status: string;
	communication_status: string;
	eur_amount: number;
	paid_amount: number;
};

export type TSendEmailForInvoiceRequest = {
	invoiceId: number;
	body: TSendEmailForInvoiceBody;
};

export type TSendEmailForInvoiceResponse = {
	[key: string]: unknown;
};

export type TSendEmailForInvoiceResponsePayload = TSendEmailForInvoiceResponse;

// ==========================================
// Update Invoice GPSR
// ==========================================

export type TUpdateInvoiceGpsrBody = {
	tableName: string;
	data: {
		communication_status: string;
		id: number;
	};
};

export type TUpdateInvoiceGpsrRequest = {
	body: TUpdateInvoiceGpsrBody;
};

export type TUpdateInvoiceGpsrResponse = {
	[key: string]: unknown;
};

export type TUpdateInvoiceGpsrResponsePayload = TUpdateInvoiceGpsrResponse;

// ==========================================
// Encrypt Invoice Number
// ==========================================

export type TEncryptInvoiceNumberRequest = {
	invoiceNumber: number;
};

export type TEncryptInvoiceNumberResponse = {
	encryptedInvoceNumber: string;
	paymentLink: string;
};

export type TEncryptInvoiceNumberResponsePayload = TEncryptInvoiceNumberResponse | null;

// ==========================================
// Nullify Invoice
// ==========================================

export type TNullifyInvoiceBody = {
	invoiceId: number;
	comment: string;
};

export type TNullifyInvoiceRequest = {
	body: TNullifyInvoiceBody;
	invoiceListRequest: TInvoiceListRequest;
};

export type TNullifyInvoiceResponse = {
	[key: string]: unknown;
};

export type TNullifyInvoiceResponsePayload = TNullifyInvoiceResponse;

// ==========================================
// Remove Invoice LPF
// ==========================================

export type TRemoveInvoiceLpfBody = {
	invoiceId: number;
	comment: string;
};

export type TRemoveInvoiceLpfRequest = {
	body: TRemoveInvoiceLpfBody;
	invoiceListRequest: TInvoiceListRequest;
};

export type TRemoveInvoiceLpfResponse = {
	[key: string]: unknown;
};

export type TRemoveInvoiceLpfResponsePayload = TRemoveInvoiceLpfResponse;

// ==========================================
// Invoice List Excel Export
// ==========================================

export type TInvoiceListExcelExportRequest = {
	tableName: string;
	filter?: TFiltersQ4Us;
};

export type TInvoiceListExcelExportResponse = Blob;

export type TInvoiceListExcelExportResponsePayload = TInvoiceListExcelExportResponse | null;

// ==========================================
// Download Invoice Archive
// ==========================================

export type TDownloadInvoiceArchiveRequest = {
	filename: string;
};

export type TDownloadInvoiceArchiveResponse = Blob;

// ==========================================
// Credit Notes
// ==========================================

export type TCreditNoteSearchRequest = IRequestWithPageSortFilterSearch;

export type TCreditNoteUser = {
	id: string;
	email: string | null;
	fullName: string | null;
};

export type TCreditNoteInvoice = {
	invoiceId: number;
	invoiceNumber: number | null;
	creditedAmount: number;
	creditedVat: number;
	vatRate: number;
};

export type TCreditNote = {
	id: number;
	number: number;
	emId: number;
	processingStatus: string | null;
	processingMethod: string | null;
	crTotalAmount: number;
	crTotalVat: number;
	comments: string | null;
	paymentInfo: string | null;
	refundedInvoiceId: number | null;
	createdAt: string;
	createdBy: TCreditNoteUser | null;
	updatedAt: string | null;
	updatedBy: TCreditNoteUser | null;
	invoices: TCreditNoteInvoice[] | null;
};

export type TCreditNoteListResponse = TPageResponse<TCreditNote>;

export type TCreditNoteIdRequest = {
	id: number;
};
